<template>
  <content-modal
    class="cropping-canvas-modal"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :auto-hide-off="autoHideOff"
    :show-header="false"
    :show-close="false"
    full-screen
    @modal-hide-request="actionHideRequest"
  >
    <template #content>
      <modal-content>
        <template #content>
          <cropping-canvas
            :crop-task="cropTask"
            :kind="kind"
            :fixed-crop-size="fixedCropSize"
            :export-size="exportSize"
            :file="params.file"
            :initial-aspect-ratio="initialAspectRatio"
            :special-note-text-label="specialNoteTextLabel"
            @crop="handleCropped"
          />
        </template>
        <template #tools>
          <cv-button
            class="cancel"
            kind="secondary"
            size="field"
            :disabled="dataSaveButton.isLoading"
            @click="hide"
          >
            {{ secondaryButtonLabel }}
          </cv-button>
          <fetch-data-button
            kind="primary"
            size="field"
            :disabled="dataSaveButton.isLoading"
            :loading="dataSaveButton.isLoading"
            :error="dataSaveButton.error"
            @click="handleCrop"
          >
            <span v-if="dataSaveButton.error">{{ t("button_state_error") }}</span>
            <span v-else>{{ primaryButtonLabel }}</span>
          </fetch-data-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import CroppingCanvas from "@plugins/cropping/components/CroppingCanvas.vue";
import DOMUtils from "@utils/dom.utils";
import modalEventBus from "@plugins/modal/service/event-bus";
import { CroppingToolKind } from "@plugins/cropping/cropping.constant";
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import { useVm } from "@utils/vm.ts";

// Props
const props = defineProps({
  params: { type: Object, default: null },
});

//Emits
const emit = defineEmits(["hide", "cropped"]);

const { t } = useI18n();
const { vmNode } = useVm();
const modal = useModal();

// Data
const closeAriaLabel = ref("Close");
const size = ref("large");
const autoHideOff = ref(true);
const visible = ref(true);
const cropTask = ref("");
const dataSaveButton = ref({
  kind: "primary",
  tipPosition: "top",
  tipAlignment: "end",
  isLoading: false,
});

// Computed
const kind = computed(() => props.params.croppingConfiguration?.kind ?? CroppingToolKind.basic.key);
const fixedCropSize = computed(() => props.params.croppingConfiguration?.fixedCropSize ?? null);
const exportSize = computed(() => props.params.croppingConfiguration?.exportSize ?? null);
const initialAspectRatio = computed(
  () => props.params.croppingConfiguration?.initialAspectRatio ?? 0,
);

const primaryButtonLabel = computed(() => {
  return props.params.primaryBtnLabel ?? t("button_done");
});
const secondaryButtonLabel = computed(() => {
  return props.params.secondaryBtnLabel ?? t("button_cancel");
});

const specialNoteTextLabel = computed(
  () => props.params.croppingConfiguration?.specialNoteTextLabel ?? null,
);
const cropPromptConfig = computed(() => props.params?.cropPromptConfig ?? null);
const hasCropPrompt = computed(() => !!cropPromptConfig.value);

// Methods
const hide = () => {
  // restore previous scrollability
  //props.params.eventBus.$emit("hide");
  DOMUtils.removeClass("body", "bx--body--with-modal-open");
  modal.remove(props.params.modalId);
  emit("hide");
};

const actionHidden = function () {
  visible.value = false;
  modalEventBus.$emit("modal-hidden", vmNode.key);
};

const actionHideRequest = function (event) {
  // to prevent close modal upon clicking anywhere outside the modal container
  if (event["cv:reason"] !== "external-click") {
    actionHidden();
  }
};

const handleCrop = () => {
  if (hasCropPrompt.value) {
    const modalProps = {
      ...cropPromptConfig.value,
      actionPrimary: () => {
        dataSaveButton.value.isLoading = true;
        cropTask.value = "crop";
      },
      actionHidden: () => ({}),
    };
    modal.showMessage("warning", modalProps, true);
  } else {
    dataSaveButton.value.isLoading = true;
    cropTask.value = "crop";
  }
};

const handleCropped = (event) => {
  console.log("cropped", event);
  cropTask.value = "";
  emit("cropped", event);
};
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/spacing";

.fullscreen.bx--modal.content-modal.cropping-canvas-modal {
  .bx--modal-container {
    @include media("tablet-min") {
      // fallback to 100vh for browsers that do not support 100dvh
      height: calc(100vh - #{$layout-05});
      max-height: calc(100vh - #{$layout-05});

      // use 100dvh instead of 100vh to avoid issues with mobile browsers' dynamic viewport height
      height: calc(100dvh - #{$layout-05});
      max-height: calc(100dvh - #{$layout-05});
    }
  }

  .bx--modal-content {
    @include media("mobile") {
      padding-top: $spacing-05;
    }
  }
}
</style>
